import React, { FC, MouseEvent, useCallback } from "react";
import { DayModifiers, DayProps } from "react-day-picker";

import {
  StyledButtonClose,
  StyledButtonCloseColored,
  StyledButtonSave,
  StyledSelectDateButtons,
  StyledSelectDateDatePicker,
  StyledSelectDateFooter,
} from "./Styles";
import { DatePicker } from "../DatePicker/DatePicker";
import { IconButton } from "../IconButton";
import CancelCrossIcon from "../Icons/collection/CancelCross";
import { Tooltip } from "../Tooltip/Tooltip";

export interface IDatePickerForm {
  mode: "instant" | "atomic";
  saveButtonText?: string;
  cancelButtonText?: string;
  clearButtonText?: string;
  onSave?: () => void;
  onCancel?: (event: MouseEvent<HTMLElement>) => void;
  onClear?: () => void;
  onChange?: (dates?: { from: Date; to: Date }) => void;
  /** Called when day is clicked */
  onDayClick?: (day: Date, modifiers: DayModifiers) => void;
  /** Set selected day or days */
  selectedDays?: { from?: Date | undefined; to?: Date | undefined };
  /** Modifiers */
  modifiers?: DayModifiers;
  /** Render day callback */
  renderDay?: (props: DayProps) => JSX.Element | null;
  /** Set selection range */
  selectionMode?: string;
  /** Set is date required */
  dateRequired?: boolean;
  /** Set first day of week */
  firstDayOfWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** on month change callback */
  onMonthChange?: (month: Date) => void;
  /** Selected month */
  month: Date;
  disabledSaveButton?: boolean;
}

export const DatePickerForm: FC<IDatePickerForm> = ({
  mode,
  saveButtonText = "Save",
  cancelButtonText = "Cancel",
  clearButtonText = "Clear",
  onSave,
  onCancel,
  onClear,
  dateRequired = false,
  firstDayOfWeek = 0,
  selectedDays,
  selectionMode = "custom",
  onChange,
  onDayClick,
  onMonthChange,
  month,
  disabledSaveButton = false,
  modifiers,
  renderDay,
}) => {
  const handleOnKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && typeof onSave === "function") {
        onSave();
      } else if (
        (e.key === "Esc" || e.key === "Escape") &&
        typeof onCancel === "function"
      ) {
        onCancel(e);
      }
    },
    [onSave, onCancel]
  );

  return (
    <div onKeyDown={handleOnKeyDown} tabIndex={-1} className="date-picker-form">
      <StyledSelectDateDatePicker>
        <DatePicker
          month={month}
          onMonthChange={onMonthChange}
          onChange={onChange}
          onDayClick={onDayClick}
          selectedDays={selectedDays}
          selectionMode={selectionMode}
          dateRequired={dateRequired}
          firstDayOfWeek={firstDayOfWeek}
          modifiers={modifiers}
          renderDay={renderDay}
        />
      </StyledSelectDateDatePicker>
      {mode === "atomic" || !dateRequired ? (
        <StyledSelectDateFooter className="date-picker-footer" $mode={mode}>
          {mode === "atomic" ? (
            <StyledSelectDateButtons className="date-picker-buttons">
              <StyledButtonSave
                size="small"
                variant="primary"
                onClick={onSave}
                disabled={disabledSaveButton}
              >
                {saveButtonText}
              </StyledButtonSave>
              <Tooltip
                title={cancelButtonText}
                popperTooltipStyle={{ zIndex: 1301 }}
              >
                <IconButton size="small" variant="text gray" onClick={onCancel}>
                  <CancelCrossIcon />
                </IconButton>
              </Tooltip>
              {!dateRequired ? (
                <StyledButtonClose
                  size="small"
                  onClick={onClear}
                  variant="text gray"
                >
                  {clearButtonText}
                </StyledButtonClose>
              ) : null}
            </StyledSelectDateButtons>
          ) : (
            !dateRequired && (
              <StyledButtonCloseColored
                size="small"
                onClick={onClear}
                variant="text colored"
              >
                {clearButtonText}
              </StyledButtonCloseColored>
            )
          )}
        </StyledSelectDateFooter>
      ) : null}
    </div>
  );
};

DatePickerForm.displayName = "DatePickerForm";
